<!-- ai-agent-tool-box.component.html -->
<section *ngIf="reportSearch" class="h-full">

  <div *ngIf="company" class="card h-full flex flex-col w-full">

    <!-- Header Section - matching company-contact style -->
    <div class="pl-6 pt-6">
      <h2 class="text-xl font-semibold">AI Agent Tool Box</h2>
      <p class="text-sm !text-secondary dark:!text-primary-200">Select the tool that best fits your needs and unlock powerful insights.</p>
    </div>

    <!-- Error Alert -->
    <div *ngIf="error$ | async as error" class="mx-6 mt-4 bg-red-50 border-2 border-red-200 rounded-lg p-3">
      <div class="flex items-center">
        <svg class="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"/>
        </svg>
        <p class="text-sm text-red-800">{{ error.message || 'Failed to generate insights. Please try again.' }}</p>
      </div>
    </div>

    <!-- Cards Grid -->
    <div class="border-t border-violet-50 dark:border-[#3c4377] mt-6 p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">

      <!-- Dynamic Cards -->
      <div *ngFor="let tool of agentTools"
           class="bg-white border rounded-lg p-3 transition-all duration-300 flex flex-col"
           [ngClass]="{
             'border-blue-royal': isInsightGenerated(tool),
             'border-violet-50': !isInsightGenerated(tool)
           }">

        <div class="flex items-start justify-between mb-1">
          <h3 class="text-sm font-semibold text-gray-900 leading-tight">
            {{ tool.title }}
          </h3>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
               class="w-4 h-4 text-primary flex-shrink-0 ml-2 cursor-help"
               [matTooltip]="tool.description"
               matTooltipPosition="above"
               matTooltipClass="custom-tooltip">
            <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clip-rule="evenodd" />
          </svg>
        </div>

        <span *ngIf="!isInsightGenerated(tool)" class="text-xs !text-blue-royal font-semibold pb-6">{{ tool.credits }} credits</span>
        <span *ngIf="isInsightGenerated(tool)" class="text-xs !text-primary font-semibold pb-6">Generated</span>

        <div class="mt-auto">
          <!-- Show Open button when insight is generated -->
          <div *ngIf="isInsightGenerated(tool)">
            <button class="btn bg-blue-royal text-white text-xs font-normal py-2 px-4 w-32 text-center" (click)="viewContent(tool)">Open</button>
          </div>

          <!-- Show button when insight is not generated -->
          <button *ngIf="!isInsightGenerated(tool)"
                  class="btn bg-blue-royal text-white text-xs font-normal py-2 px-4 w-32 text-center"
                  [ngClass]="{
                    'disabled opacity-75': isButtonDisabled(tool, false) || isToolGenerating(tool),
                    'cursor-not-allowed': !isInsightAvailable(tool) || isToolGenerating(tool),
                    'pointer-events-none': isToolGenerating(tool)
                  }"
                  [disabled]="isButtonDisabled(tool, false) || isToolGenerating(tool)"
                  [matTooltip]="tool.tooltipText || (!isInsightAvailable(tool) ? 'This insight is not available' : '')"
                  matTooltipPosition="above"
                  matTooltipClass="custom-tooltip"
                  (click)="useAgent(tool)">
            {{ getButtonText(tool) }}
          </button>
        </div>
      </div>

      <!-- Sales Qualify Card (Sales Qualifying Agent → SQL score) — only for allowlisted accounts -->
      <div *ngIf="sqlState.enabled === true" class="bg-white border rounded-lg p-3 transition-all duration-300 flex flex-col"
           [ngClass]="{
             'border-blue-royal': sqlState.qualification?.status === 'completed',
             'border-amber-300': sqlState.qualification?.status === 'needs_input',
             'border-violet-50': !sqlState.qualification || (sqlState.qualification?.status !== 'completed' && sqlState.qualification?.status !== 'needs_input')
           }">
        <div class="flex items-start justify-between mb-1">
          <h3 class="text-sm font-semibold text-gray-900 leading-tight">Sales Qualify</h3>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
               class="w-4 h-4 text-primary flex-shrink-0 ml-2 cursor-help"
               matTooltip="Guided agent that qualifies the company to a Sales Qualified Lead: what they sell, deal value, customisation and sales scalability — from the profile, their website and Google, asking you only what it cannot settle."
               matTooltipPosition="above"
               matTooltipClass="custom-tooltip">
            <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clip-rule="evenodd" />
          </svg>
        </div>
        <span class="text-xs font-semibold pb-6"
              [ngClass]="{
                '!text-blue-royal': !sqlState.qualification || sqlState.qualification?.status === 'failed',
                '!text-primary': sqlState.qualification?.status === 'completed',
                '!text-amber-600': sqlState.qualification?.status === 'needs_input',
                '!text-gray-500': sqlIsRunning
              }">{{ sqlStatusLabel }}</span>
        <div class="mt-auto">
          <button class="btn bg-blue-royal text-white text-xs font-normal py-2 px-4 w-32 text-center"
                  [disabled]="!sqlState.loaded && sqlState.loading"
                  (click)="openSalesQualify()">
            {{ sqlButtonText }}
          </button>
        </div>
      </div>

      <!-- Enrich Profile Card -->
      <div class="bg-white border rounded-lg p-3 transition-all duration-300 flex flex-col"
           [ngClass]="{
             'border-blue-royal': reportSearch?.is_enrich === 1,
             'border-violet-50': reportSearch?.is_enrich !== 1
           }">
        <div class="flex items-start justify-between mb-1">
          <h3 class="text-sm font-semibold text-gray-900 leading-tight">Enrich Profile</h3>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
               class="w-4 h-4 text-primary flex-shrink-0 ml-2 cursor-help"
               matTooltip="Leverage our advanced AI technology to automatically enhance the profile with relevant insights, skills, and achievements."
               matTooltipPosition="above"
               matTooltipClass="custom-tooltip">
            <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clip-rule="evenodd" />
          </svg>
        </div>
        <span *ngIf="reportSearch?.is_enrich !== 1" class="text-xs !text-blue-royal font-semibold pb-6">2 credits</span>
        <span *ngIf="reportSearch?.is_enrich === 1" class="text-xs !text-primary font-semibold pb-6">Enriched</span>
        <div class="mt-auto">
          <button class="btn bg-blue-royal text-white text-xs font-normal py-2 px-4 w-32 text-center"
                  [disabled]="reportSearch?.is_enrich === 1 || isEnriching"
                  [ngClass]="{'cursor-not-allowed': reportSearch?.is_enrich === 1 || isEnriching}"
                  (click)="onEnrichProfile()">
            {{ isEnriching ? 'Enriching...' : (reportSearch?.is_enrich === 1 ? 'Enriched' : 'Enrich Profile') }}
          </button>
        </div>
      </div>

      <!-- Custom Agent Card -->
      <div class="bg-white border rounded-lg p-3 transition-all duration-300 flex flex-col border-violet-50">
        <div class="flex items-start justify-between mb-1">
          <h3 class="text-sm font-semibold text-gray-900 leading-tight">Custom Agents</h3>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
               class="w-4 h-4 text-primary flex-shrink-0 ml-2 cursor-help"
               matTooltip="Need a custom AI agent tailored to your specific needs? Contact us to discuss your requirements."
               matTooltipPosition="above"
               matTooltipClass="custom-tooltip">
            <path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clip-rule="evenodd" />
          </svg>
        </div>
        <span class="text-xs !text-blue-royal font-semibold pb-6">Custom implementation</span>
        <div class="mt-auto">
          <a href="https://salesommo.com/contact/" target="_blank" rel="noopener noreferrer"
             class="btn bg-blue-royal !text-white text-xs font-normal py-2 px-4 w-32 text-center">
            Contact
          </a>
        </div>
      </div>

    </div>

  </div>

  <!-- Special box for startups looking for investors Results -->
  <section *ngIf="showSpecialBox" class="blue-card p-6 h-full mt-6 mb-6">
    <dl class="flex flex-wrap">
      <div class="flex flex-col justify-between w-full">

        <div class="flex justify-between w-full mb-6">
          <!--
          <h2 class="text-xl text-secondary-900 dark:text-white font-semibold">
            Special box for startups looking for investors
          </h2>
          -->
          <h2 class="text-xl text-secondary-900 dark:text-white font-semibold">
            Startups looking for investors
          </h2>
        </div>

        <div *ngIf="company">
          <!-- Keep the assistant prompt component -->
          <app-assistant-prompt tag="ASK_ASSISTANT_SPECIAL_BOX"
                                title="Special information"
                                [type]="'special-box'"
                                [company]="company"
                                [showButton]="true"
                                [buttonType]="1"
                                [reportId]="reportSearch?.id">
          </app-assistant-prompt>
        </div>

      </div>
    </dl>
  </section>

  <!-- Email Sequence Insight Results -->
  <section *ngIf="showEmailSequenceBox" class="blue-card p-6 h-full mt-6 mb-6">
    <dl class="flex flex-wrap">
      <div class="flex flex-col justify-between w-full">

        <div class="flex justify-between w-full mb-6">
          <h2 class="text-xl text-secondary-900 dark:text-white font-semibold">
            Email Sequence
          </h2>
        </div>

        <div *ngIf="company">
          <!-- Keep the assistant prompt component -->
          <app-assistant-prompt tag="ASK_ASSISTANT_EMAIL_SEQUENCE"
                                title="Email sequence insights"
                                [type]="'email-sequence'"
                                [company]="company"
                                [showButton]="true"
                                [buttonType]="1"
                                [reportId]="reportSearch?.id">
          </app-assistant-prompt>
        </div>

      </div>
    </dl>
  </section>

</section>
